/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import { Input, Button } from '@material-tailwind/react';
import { sha256 } from 'js-sha256';
import React, { useState } from 'react';
import { NewsletterBackground } from './background';
import { ComponentNewsletterSubscription } from '$/(cms)/_modules/cmsService/types';
import { tr } from '$/components/cms/helpers';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { useClientContext } from '$/modules/context/client';

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export const NewsletterSubscription = ({
  title,
  description,
  bg_image,
  notes,
}: ComponentNewsletterSubscription) => {
  const { lang } = useClientContext();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEmailChange = (e: any) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    const normalizedEmail = normalizeEmail(email);

    pushDataLayer({
      event: 'form_submit',
      lead_form_name: 'newsletterSubscriptionSS',
      form_type: 'subscription form',
      action: 'submit',
      marketing_consent: 'yes',
      user_data: {
        email: normalizedEmail,
        email_hash_sha256: sha256(normalizedEmail),
      },
      user_identities: {
        email: normalizedEmail,
      },
    });

    try {
      // user email in segment will be overridden by this during identify call in segment.
      // discuss with product team how it should work
      // segment.marketingConsent({ email: normalizedEmail });
      console.error('Newsletter subscription not implemented');

      setEmail('');
      const successMessage = `${tr('subscription_successful', lang)}!`;
      setSuccess(successMessage);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // Handle error
      setError(err.response?.data?.error || `${tr('error_form', lang)}.`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="relative xl:min-h-100">
      <NewsletterBackground bg_url={`url(${bg_image?.url})`} />
      <div className="relative mx-auto grid max-w-screen-2xl grid-cols-1 px-4 py-8 sm:py-11 md:grid-cols-2 md:px-8 md:py-16 lg:py-14">
        <div className="hidden sm:block"></div>

        <div className="flex justify-center md:justify-end">
          <div className="!w-full lg:max-w-[530px] xl:max-w-[554px]">
            <h3 className="mhg-alpha-typography-display-1 mb-1 text-gray-900">
              {title}
            </h3>
            <p className="mhg-alpha-typography-body-1 mb-2 font-medium text-gray-600">
              {description}
            </p>

            <div className="flex flex-col justify-start">
              <Input
                onChange={handleEmailChange}
                value={email}
                type="email"
                placeholder={tr('enter_your_email', lang) ?? ''}
                containerProps={{
                  className: '!min-w-[unset] md:!min-w-[200px]',
                }}
                className="newsletter-input w-full rounded-lg border border-gray-400 font-medium bg-white"
              />
              <Button
                disabled={loading}
                onClick={handleSubmit}
                className="mt-2 w-full md:mt-4"
              >
                {loading
                  ? `${tr('submitting', lang)}... `
                  : tr('subscribe', lang)}
              </Button>
            </div>

            {error && (
              <p className="mt-2 w-full text-center font-bold text-red">
                {error}
              </p>
            )}
            {success && (
              <p className="mt-2 w-full text-center font-bold text-primary">
                {success}
              </p>
            )}

            {notes && (
              <div
                className="mt-2 flex text-left text-gray-600 text-font-size/badge md:mt-4 md:text-font-size/sm"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: notes as string,
                }}
              ></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
